import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { isImageUrl, mediaId, resolveImageUrl } from '@easytools/tool-content';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

export interface ContentImage {
    id: number;
    ref: string;
    toolId: number | null;
    toolName: string | null;
    fileName: string;
    alt: string | null;
    contentType: string;
    width: number;
    height: number;
    fileSizeBytes: number;
    uploadedBy: string | null;
    uploadedAt: string;
}

interface Usage { toolId: number; toolName: string; kind: string; language: string; state: string }

export interface PickedImage { url: string; alt?: string }

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';
const KIND_LABEL: Record<string, string> = { card: 'scheda', publicPage: 'pagina pubblica', guide: 'guida' };

const errorMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error
    ?? (error instanceof Error ? error.message : fallback);

const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// ─── Contesto: qualunque campo immagine può aprire la libreria ───────────────

type Pick = (current?: string) => Promise<PickedImage | null>;
interface MediaPickerValue { pick: Pick; upload: (file: File) => Promise<PickedImage | null> }

const MediaPickerContext = createContext<MediaPickerValue | null>(null);

export const useMediaPicker = () => useContext(MediaPickerContext);

/** Rende disponibile la libreria immagini ai campi dell'editor (ImageField). */
export const MediaPickerProvider: React.FC<{ toolId: number; children: React.ReactNode }> = ({ toolId, children }) => {
    const { showToast } = useToast();
    const [request, setRequest] = useState<{ current?: string; resolve: (value: PickedImage | null) => void } | null>(null);

    const pick = useCallback<Pick>(current => new Promise(resolve => setRequest({ current, resolve })), []);
    const upload = useCallback(async (file: File) => {
        try {
            const image = await apiService.adminUploadContentImage<ContentImage>(file, toolId);
            showToast('Immagine caricata', image.fileName, 'success');
            return { url: image.ref, alt: image.alt ?? undefined };
        } catch (error) {
            showToast('Immagine non caricata', errorMessage(error, 'Riprova'), 'danger');
            return null;
        }
    }, [toolId, showToast]);

    const close = (value: PickedImage | null) => {
        request?.resolve(value);
        setRequest(null);
    };

    const value = useMemo(() => ({ pick, upload }), [pick, upload]);
    return (
        <MediaPickerContext.Provider value={value}>
            {children}
            <MediaLibraryDialog show={request !== null} toolId={toolId} current={request?.current} onPick={close} />
        </MediaPickerContext.Provider>
    );
};

// ─── Finestra della libreria ─────────────────────────────────────────────────

/**
 * Libreria immagini: carica dal PC (anche trascinando i file), cerca, scegli. Le immagini
 * del tool e quelle di uso comune stanno insieme; "Tutte" mostra anche quelle degli altri tool.
 */
const MediaLibraryDialog: React.FC<{
    show: boolean;
    toolId: number;
    current?: string;
    onPick: (value: PickedImage | null) => void;
}> = ({ show, toolId, current, onPick }) => {
    const { showToast } = useToast();
    const [images, setImages] = useState<ContentImage[] | null>(null);
    const [scope, setScope] = useState<'tool' | 'all'>('tool');
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<number | null>(null);
    const [uploading, setUploading] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const ids = { search: useId(), alt: useId() };

    const load = useCallback(async () => {
        try {
            setImages(await apiService.adminListContentImages<ContentImage>({ toolId: scope === 'tool' ? toolId : undefined, q: query }));
        } catch (error) {
            setImages([]);
            showToast('Libreria non caricata', errorMessage(error, 'Riprova'), 'danger');
        }
    }, [scope, toolId, query, showToast]);

    useEffect(() => {
        if (!show) return;
        const timer = window.setTimeout(() => void load(), query ? 250 : 0);
        return () => window.clearTimeout(timer);
    }, [show, load, query]);

    useEffect(() => { if (show) setSelected(mediaId(current)); }, [show, current]);

    const uploadFiles = async (files: FileList | File[]) => {
        const list = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!list.length) { showToast('Nessuna immagine', 'Trascina file PNG, JPEG, WebP, GIF o SVG', 'warning'); return; }
        setUploading(list.length);
        let last: ContentImage | null = null;
        for (const file of list) {
            try {
                last = await apiService.adminUploadContentImage<ContentImage>(file, toolId);
            } catch (error) {
                showToast(`${file.name} non caricata`, errorMessage(error, 'Riprova'), 'danger');
            }
            setUploading(n => n - 1);
        }
        await load();
        if (last) setSelected(last.id);
    };

    const chosen = images?.find(i => i.id === selected) ?? null;

    const patch = async (image: ContentImage, dto: { alt?: string; toolId?: number }) => {
        try {
            const updated = await apiService.adminUpdateContentImage<ContentImage>(image.id, dto);
            setImages(prev => prev?.map(i => (i.id === updated.id ? updated : i)) ?? prev);
        } catch (error) {
            showToast('Immagine non aggiornata', errorMessage(error, 'Riprova'), 'danger');
        }
    };

    const remove = async (image: ContentImage) => {
        if (!window.confirm(`Eliminare ${image.fileName} dalla libreria?`)) return;
        try {
            await apiService.adminDeleteContentImage(image.id);
        } catch (error) {
            const usage = (error as { response?: { status?: number; data?: { usage?: Usage[] } } })?.response;
            if (usage?.status !== 409) { showToast('Immagine non eliminata', errorMessage(error, 'Riprova'), 'danger'); return; }
            const where = (usage.data?.usage ?? []).map(u => `• ${u.toolName}: ${KIND_LABEL[u.kind] ?? u.kind} ${u.language.toUpperCase()} (${u.state === 'published' ? 'pubblicata' : 'bozza'})`).join('\n');
            if (!window.confirm(`L'immagine è usata in:\n${where}\n\nSe la elimini, lì non comparirà più. Eliminarla comunque?`)) return;
            try {
                await apiService.adminDeleteContentImage(image.id, true);
            } catch (inner) {
                showToast('Immagine non eliminata', errorMessage(inner, 'Riprova'), 'danger');
                return;
            }
        }
        setSelected(null);
        await load();
        showToast('Immagine eliminata', image.fileName, 'success');
    };

    return (
        <Modal show={show} onHide={() => onPick(null)} size="xl" scrollable className="tcs-media">
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">Libreria immagini</Modal.Title>
            </Modal.Header>
            <Modal.Body
                className={dragOver ? 'is-drop' : undefined}
                onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={e => { if (e.currentTarget === e.target) setDragOver(false); }}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) void uploadFiles(e.dataTransfer.files); }}
            >
                <div className="tcs-media__bar">
                    <label htmlFor={ids.search} className="visually-hidden">Cerca</label>
                    <input id={ids.search} type="search" className="form-control form-control-sm" placeholder="Cerca per nome o descrizione"
                        value={query} onChange={e => setQuery(e.target.value)} />
                    <div className="tcs-choice__options" role="radiogroup" aria-label="Quali immagini">
                        <button type="button" role="radio" aria-checked={scope === 'tool'} className={`tcs-choice__option${scope === 'tool' ? ' is-on' : ''}`} onClick={() => setScope('tool')}>Questo tool e comuni</button>
                        <button type="button" role="radio" aria-checked={scope === 'all'} className={`tcs-choice__option${scope === 'all' ? ' is-on' : ''}`} onClick={() => setScope('all')}>Tutte</button>
                    </div>
                    <input ref={fileInput} type="file" accept={ACCEPT} multiple hidden onChange={e => { if (e.target.files) void uploadFiles(e.target.files); e.target.value = ''; }} />
                    <button type="button" className="btn btn-sm btn-primary" disabled={uploading > 0} onClick={() => fileInput.current?.click()}>
                        {uploading > 0 ? <><span className="spinner-border spinner-border-sm me-2" />Carico {uploading}…</> : <><i className="bi bi-upload me-1" aria-hidden="true" />Carica dal PC</>}
                    </button>
                </div>
                <p className="tcs-media__hint">Trascina qui le immagini per caricarle. PNG, JPEG, WebP, GIF o SVG, fino a 8 MB.</p>

                <div className="tcs-media__body">
                    {images === null ? (
                        <p className="tcs-media__empty"><span className="spinner-border spinner-border-sm me-2" />Caricamento…</p>
                    ) : images.length === 0 ? (
                        <div className="tcs-media__empty">
                            <i className="bi bi-images" aria-hidden="true" />
                            <strong>{query ? 'Nessuna immagine trovata' : 'La libreria è vuota'}</strong>
                            <span>{query ? 'Prova con un’altra parola.' : 'Carica la prima immagine dal PC o trascinala qui.'}</span>
                        </div>
                    ) : (
                        <ul className="tcs-media__grid" role="listbox" aria-label="Immagini">
                            {images.map(image => (
                                <li key={image.id} role="option" aria-selected={selected === image.id}>
                                    <button type="button" className={`tcs-media__item${selected === image.id ? ' is-on' : ''}`}
                                        onClick={() => setSelected(image.id)} onDoubleClick={() => onPick({ url: image.ref, alt: image.alt ?? undefined })}>
                                        <span className="tcs-media__thumb"><img src={resolveImageUrl(image.ref)} alt="" loading="lazy" /></span>
                                        <span className="tcs-media__name">{image.fileName}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {chosen && (
                        <aside className="tcs-media__detail" aria-label="Immagine scelta">
                            <img src={resolveImageUrl(chosen.ref)} alt="" />
                            <dl>
                                <dt>File</dt><dd>{chosen.fileName}</dd>
                                <dt>Misure</dt><dd>{chosen.width ? `${chosen.width} × ${chosen.height} px · ` : 'Vettoriale · '}{formatSize(chosen.fileSizeBytes)}</dd>
                                <dt>Di</dt><dd>{chosen.toolName ?? 'Uso comune'}</dd>
                            </dl>
                            <div className="tcs-fields__field">
                                <label className="tcs-fields__label" htmlFor={ids.alt}>Descrizione per chi non vede l’immagine</label>
                                <input key={chosen.id} id={ids.alt} className="form-control form-control-sm" defaultValue={chosen.alt ?? ''}
                                    placeholder="Cosa mostra l’immagine"
                                    onBlur={e => { if (e.target.value.trim() !== (chosen.alt ?? '')) void patch(chosen, { alt: e.target.value }); }} />
                                <div className="tcs-fields__hint">Si propone come testo alternativo quando la inserisci.</div>
                            </div>
                            <button type="button" className="btn btn-sm btn-light"
                                onClick={() => void patch(chosen, { toolId: chosen.toolId ? 0 : toolId })}>
                                {chosen.toolId ? 'Rendila di uso comune' : 'Associala a questo tool'}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => void remove(chosen)}>
                                <i className="bi bi-trash me-1" aria-hidden="true" />Elimina dalla libreria
                            </button>
                        </aside>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={() => onPick(null)}>Annulla</button>
                <button type="button" className="btn btn-primary" disabled={!chosen}
                    onClick={() => chosen && onPick({ url: chosen.ref, alt: chosen.alt ?? undefined })}>
                    Usa questa immagine
                </button>
            </Modal.Footer>
        </Modal>
    );
};

// ─── Campo immagine per il pannello delle proprietà ──────────────────────────

/**
 * Un'immagine di un blocco: anteprima, scelta dalla libreria, caricamento trascinando un
 * file sul campo, e l'indirizzo a mano per chi lo vuole. Se il blocco non ha un testo
 * alternativo, onChange riceve anche quello salvato nella libreria: una sola modifica.
 */
export const ImageField: React.FC<{
    label: string;
    value: string;
    onChange: (url: string, alt?: string) => void;
    /** Testo alternativo attuale del blocco: se è vuoto si propone quello della libreria. */
    alt?: string;
}> = ({ label, value, onChange, alt }) => {
    const picker = useMediaPicker();
    const id = useId();
    const [manual, setManual] = useState(() => Boolean(value) && mediaId(value) === null);
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const preview = value && isImageUrl(value) ? resolveImageUrl(value) : null;

    const apply = (picked: PickedImage | null) => {
        if (!picked) return;
        onChange(picked.url, !alt?.trim() && picked.alt ? picked.alt : undefined);
    };

    return (
        <div className="tcs-fields__field">
            <span className="tcs-fields__label" id={`${id}-label`}>{label}</span>
            <div className={`tcs-image${dragOver ? ' is-drop' : ''}`} aria-labelledby={`${id}-label`}
                onDragOver={e => { if (picker && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={() => setDragOver(false)}
                onDrop={async e => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer.files[0];
                    if (!picker || !file) return;
                    setBusy(true);
                    apply(await picker.upload(file));
                    setBusy(false);
                }}>
                <span className="tcs-image__preview">
                    {busy ? <span className="spinner-border spinner-border-sm" />
                        : preview ? <img src={preview} alt="" /> : <i className="bi bi-image" aria-hidden="true" />}
                </span>
                <span className="tcs-image__actions">
                    <button type="button" className="btn btn-sm btn-light" disabled={!picker}
                        onClick={async () => apply(await picker!.pick(value))}>
                        {value ? 'Cambia' : 'Scegli dalla libreria'}
                    </button>
                    {value && <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => onChange('')}>Togli</button>}
                    <span className="tcs-image__hint">{value ? (mediaId(value) !== null ? 'Dalla libreria' : 'Indirizzo esterno') : 'oppure trascina qui un file'}</span>
                </span>
            </div>
            {manual ? (
                <input id={id} className="form-control form-control-sm mt-1" value={value} placeholder="https://… oppure /percorso"
                    aria-label={`${label}: indirizzo`} onChange={e => onChange(e.target.value)} />
            ) : (
                <button type="button" className="btn btn-link btn-sm p-0 align-self-start tcs-image__manual" onClick={() => setManual(true)}>
                    Usa un indirizzo
                </button>
            )}
        </div>
    );
};

